/**
 * Reports > Store Analytics (/reports/store-analytics).
 *
 * Returns, discounts and bulk penetration by store, in three sections of one page:
 * Returns (return % and dollars returned by fiscal week or day), Discounts (dollars
 * discounted and times used, by store and by discount) and Bulk penetration (the Bulk
 * division's share of sales for week, month, quarter and year to date, with the mix of
 * every division). Replaces the QuickSight dashboards "Returns Dashboard", "Discount
 * Dashboard" and "Bulk Penetration Performance".
 *
 * This page is deliberately thin. The screen itself is the shared `StoreAnalytics`
 * component in `@ampersandbrands/ui` (presentational, so The Sweet Spot can reuse it);
 * this file fetches through `useStoreAnalytics`, keeps the filters in the URL so a view
 * is a shareable link, and adds the page chrome: header with the pin toggle, the daily
 * email control, and the Excel download (every section).
 *
 * Every window ends at the newest loaded day, never at today. Comparison is the same
 * days last year; there is no budget, because plan data stopped 2026-05-02. There are no
 * districts: the rows are stores.
 *
 * RBAC: `reports.storeanalytics` sees the whole fleet. A store manager (holding
 * `stores.sweetspot`) reaches an own-store view, enforced server-side like OMNI
 * Performance, so the store picker is hidden when the api reports store scope.
 *
 * Resilience: wrapped in an ErrorBoundary and degrades to a quiet "not available yet"
 * card on 404/503 while the api ships.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StoreAnalytics as StoreAnalyticsScreen, type StoreAnalyticsFilters } from '@ampersandbrands/ui';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ReportSubscribeControl } from '@/components/ReportSubscribeControl';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { useRetailStores } from '@/lib/useRetailStores';
import { useReportPageState } from '@/lib/reportPageState';
import { isNotAvailable } from '@/lib/useStores';
import { STORE_ANALYTICS_REPORT_TYPE } from '@/lib/useReportSubscriptions';
import {
  downloadStoreAnalyticsXlsx,
  parseStoreAnalyticsFilters,
  storeAnalyticsSubscriptionFilters,
  useStoreAnalytics,
  writeStoreAnalyticsFilters,
} from '@/lib/useStoreAnalytics';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { AccessDeniedCard, ErrorCard, NotAvailableCard, StoresHeader } from '../stores/storesShared';

/** The accessSlug that grants this report (fleet view). */
const SLUG = 'reports.storeanalytics';

/**
 * Fleet through `reports.storeanalytics`, or an own-store view through
 * `stores.sweetspot` (the same dual audience as OMNI Performance). The api enforces the
 * real scope; this only decides whether to show the page.
 */
function canViewStoreAnalytics(perms: string[] | undefined, superAdmin = false): boolean {
  if (hasAccessSlug(perms, SLUG, superAdmin)) return true;
  return !!perms?.includes('stores.sweetspot');
}

const SECTION_LABEL = { returns: 'Returns', discounts: 'Discounts', bulk: 'Bulk penetration' } as const;

function StoreAnalyticsInner() {
  const { user } = useSession();
  const canView = canViewStoreAnalytics(user?.permissions, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseStoreAnalyticsFilters(searchParams), [searchParams]);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const setFilters = useCallback(
    (next: StoreAnalyticsFilters) =>
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          writeStoreAnalyticsFilters(sp, next);
          return sp;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const dataQ = useStoreAnalytics(filters, canView);
  const storesQ = useRetailStores();
  // A store-scoped caller sees one store, so the picker would only mislead.
  const storeOptions = useMemo(
    () =>
      dataQ.report?.scope === 'store'
        ? []
        : [...storesQ.stores]
            .sort((a, b) => Number(a.store_no) - Number(b.store_no))
            .map((s) => ({ store_no: String(s.store_no), store_name: s.store_name ?? '' })),
    [storesQ.stores, dataQ.report?.scope],
  );

  const pageState = useMemo(() => {
    const window =
      filters.section === 'bulk'
        ? `As of ${dataQ.report?.bulk?.through ?? (filters.through || 'newest loaded day')}`
        : (() => {
            const r = (filters.section === 'returns' ? dataQ.report?.returns : dataQ.report?.discounts)?.range;
            return r ? `${r.from} to ${r.to}` : 'Default window';
          })();
    return [
      { label: 'Section', value: SECTION_LABEL[filters.section] },
      { label: 'Window', value: window },
      { label: 'Comparison', value: filters.compare === 'none' ? 'None' : 'Same days last year' },
      { label: 'Stores', value: filters.store_no.length > 0 ? filters.store_no.join(', ') : 'All stores' },
    ];
  }, [filters, dataQ.report]);
  useReportPageState(pageState);

  async function handleExport() {
    setExporting(true);
    setToast(null);
    try {
      await downloadStoreAnalyticsXlsx(filters);
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setExporting(false);
    }
  }

  if (user && !canView) return <AccessDeniedCard what="Store Analytics" />;

  const header = (
    <StoresHeader
      eyebrow="Reports"
      title="Store Analytics"
      subtitle="Returns, discounts, and bulk penetration by store, compared with the same days last year. Refreshed every hour."
      actions={<ToolPinToggle toolSlug="store-analytics" toolName="Store Analytics" />}
    />
  );

  if (isNotAvailable(dataQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <NotAvailableCard what="Store Analytics" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}
      {toast && (
        <p role="alert" className="mb-3 text-sm text-red-700 dark:text-red-300">
          {toast}
        </p>
      )}
      <StoreAnalyticsScreen
        report={dataQ.report}
        loading={dataQ.isLoading}
        error={dataQ.error ? <ErrorCard message={dataQ.error.message} /> : undefined}
        filters={filters}
        onFiltersChange={setFilters}
        storeOptions={storeOptions}
        onExport={() => void handleExport()}
        exporting={exporting}
        toolbarExtra={
          <ReportSubscribeControl
            reportType={STORE_ANALYTICS_REPORT_TYPE}
            filters={storeAnalyticsSubscriptionFilters(filters)}
            subscribeHint="Get the workbook (returns, discounts, bulk penetration) by email every morning, saving the store, column and comparison choices you have applied. The email always covers the standard windows as of that morning."
          />
        }
      />
    </div>
  );
}

export function StoreAnalytics() {
  return (
    <ErrorBoundary>
      <StoreAnalyticsInner />
    </ErrorBoundary>
  );
}
